// The one engine service every provider shares: the UCI handshake, option
// handling, one search at a time with a newer search superseding the one
// running, and the stream of updates. A provider supplies only the
// transport: how a line of text reaches the engine and comes back.
import type { Engine } from './interface.ts';
import type { AnalyseOptions, EngineInfo, EngineLine, EngineOption, EnginePosition, EngineUpdate } from './types.ts';
import { lineFromInfo, parseUci, uci } from './uci.ts';

/** How lines travel to and from an engine process, Worker or sidecar. */
export interface UciTransport {
  send(line: string): void;
  /** Subscribe to lines the engine writes. Returns the unsubscribe. */
  onLine(listener: (line: string) => void): () => void;
  /** Shut the engine down. */
  dispose?(): void | Promise<void>;
}

export interface UciEngineOptions {
  /** Threads to ask for; 1 for a single-threaded build. */
  threads?: number;
  /** Options to set once after the handshake, e.g. Hash. */
  options?: Record<string, string | number | boolean>;
}

/** A search in progress: who gets its updates, and how it ends. */
interface Search {
  multiPv: number;
  lines: Map<number, EngineLine>;
  depth: number;
  push(update: EngineUpdate | null): void;
}

export function createUciEngine(transport: UciTransport, opts: UciEngineOptions = {}): Engine {
  const threads = opts.threads ?? 1;
  let name = '';
  let author: string | undefined;
  const options: EngineOption[] = [];
  let multiPv = 1;
  let ready: Promise<void> | undefined;
  let search: Search | undefined;
  /** Resolves when the running search has reported `bestmove`. */
  let searchDone: Promise<void> = Promise.resolve();
  let disposed = false;

  /** Wait for the next line matching `test`. */
  const waitFor = (test: (line: string) => boolean) =>
    new Promise<void>((resolve) => {
      const off = transport.onLine((line) => {
        if (test(line)) {
          off();
          resolve();
        }
      });
    });

  // Every line the engine writes, decoded and routed to the running search.
  transport.onLine((line) => {
    const msg = parseUci(line);
    switch (msg.kind) {
      case 'id':
        if (msg.field === 'name') name = msg.value;
        else author = msg.value;
        break;
      case 'option':
        options.push(msg.option);
        break;
      case 'info': {
        if (!search) break;
        const engineLine = lineFromInfo(msg.info);
        if (engineLine.depth < search.depth) break; // a late line from a shallower iteration
        if (engineLine.depth > search.depth) {
          search.depth = engineLine.depth;
          search.lines.clear();
        }
        search.lines.set(engineLine.rank, engineLine);
        search.push({
          depth: search.depth,
          lines: [...search.lines.values()].sort((a, b) => a.rank - b.rank),
          nodes: msg.info.nodes,
          nps: msg.info.nps,
          timeMs: msg.info.time,
        });
        break;
      }
      case 'bestmove':
        if (search) {
          search.push(null);
          search = undefined;
        }
        break;
    }
  });

  const init = () =>
    (ready ??= (async () => {
      const uciok = waitFor((l) => l.trim() === 'uciok');
      transport.send(uci.uci());
      await uciok;
      const setOptions: Record<string, string | number | boolean> = { ...(opts.options ?? {}) };
      if (options.some((o) => o.name === 'Threads')) setOptions.Threads = threads;
      for (const [key, value] of Object.entries(setOptions)) transport.send(uci.setoption(key, value));
      const readyok = waitFor((l) => l.trim() === 'readyok');
      transport.send(uci.isready());
      await readyok;
    })());

  /** Stop the running search, if any, and wait for its bestmove. */
  const stopRunning = async () => {
    if (!search) return;
    transport.send(uci.stop());
    await searchDone;
  };

  const engine: Engine = {
    async info(): Promise<EngineInfo> {
      await init();
      return { name, author, threads, options: [...options] };
    },

    analyse(position: EnginePosition, options: AnalyseOptions = {}): AsyncIterable<EngineUpdate> {
      const wanted = Math.max(1, options.multiPv ?? 1);
      // A queue of updates for the consumer, closed by a null.
      const queue: Array<EngineUpdate | null> = [];
      let wake: (() => void) | undefined;
      const push = (u: EngineUpdate | null) => {
        queue.push(u);
        wake?.();
      };
      let mine: Search | undefined;

      const start = async () => {
        if (disposed) throw new Error('engine disposed');
        await init();
        await stopRunning();
        if (multiPv !== wanted) {
          transport.send(uci.setoption('MultiPV', wanted));
          multiPv = wanted;
        }
        mine = { multiPv: wanted, lines: new Map(), depth: 0, push };
        search = mine;
        let done!: () => void;
        searchDone = new Promise<void>((r) => (done = r));
        const finished = waitFor((l) => l.startsWith('bestmove'));
        finished.then(done);
        transport.send(uci.position(position));
        transport.send(uci.go({ depth: options.depth, moveTimeMs: options.moveTimeMs }));
        options.signal?.addEventListener('abort', () => {
          if (search === mine) transport.send(uci.stop());
        });
      };

      return {
        [Symbol.asyncIterator]() {
          let started = false;
          return {
            async next(): Promise<IteratorResult<EngineUpdate>> {
              if (!started) {
                started = true;
                await start();
              }
              while (queue.length === 0) await new Promise<void>((r) => (wake = r));
              const item = queue.shift()!;
              return item === null ? { done: true, value: undefined } : { done: false, value: item };
            },
            async return(): Promise<IteratorResult<EngineUpdate>> {
              if (search === mine) await stopRunning();
              return { done: true, value: undefined };
            },
          };
        },
      };
    },

    async evaluate(position, options) {
      let last: EngineUpdate | undefined;
      for await (const update of engine.analyse(position, options)) last = update;
      return last ?? { depth: 0, lines: [] };
    },

    async stop() {
      await stopRunning();
    },

    async dispose() {
      disposed = true;
      await stopRunning();
      transport.send(uci.quit());
      await transport.dispose?.();
    },
  };
  return engine;
}
